import { SeoLandingLayout, SeoLandingPageData } from '../../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Excel trésorerie association : modèle gratuit + limites à connaître | Trezoone',
    description: 'Utilisez notre modèle Excel trésorerie pour votre association. Comprenez pourquoi Excel atteint ses limites — et quand passer à un outil dédié.',
    canonical: '/excel-tresorerie-association',
    keywords: 'excel trésorerie association, modèle excel trésorerie, tableau excel trésorerie association, fichier excel trésorerie',
  },
  hero: {
    tag: 'Excel trésorerie association',
    title: 'Excel pour votre trésorerie d\'association : utile au départ, limité à l\'usage.',
    subtitle: 'Un bon modèle Excel peut vous aider à démarrer. Voici comment en tirer le meilleur — et quand envisager autre chose.',
    ctaLabel: 'Passer à l\'étape supérieure',
  },
  pilierLink: { label: 'Guide trésorerie association', href: '/gestion-tresorerie-association' },
  sections: [
    {
      type: 'explain',
      title: 'Ce qu\'un fichier Excel trésorerie doit contenir',
      content: 'Pour une association, un bon tableau Excel de trésorerie comprend au minimum :',
      items: [
        'Onglet 1 : Plan de trésorerie mensuel (encaissements / décaissements / solde)',
        'Onglet 2 : Suivi des subventions (financeur, montant, état, date de versement)',
        'Onglet 3 : Transactions réelles importées ou saisies',
        'Formules de calcul du solde cumulé mois par mois',
        'Mise en forme conditionnelle pour visualiser les mois en tension',
      ],
    },
    {
      type: 'example',
      title: 'Modèle de tableau Excel trésorerie association',
      content: 'Structure type pour un fichier Excel de trésorerie association :',
      items: [
        'Ligne 1 : Solde initial (solde bancaire en début de mois)',
        'Lignes 2-10 : Encaissements (cotisations, subventions CAF, Région, Mairie, dons, ventes…)',
        'Lignes 11-20 : Décaissements (salaires, charges sociales, loyer, fournisseurs, assurances…)',
        'Ligne 21 : Total encaissements - Total décaissements = Flux net du mois',
        'Ligne 22 : Solde final = Solde initial + Flux net',
      ],
    },
    {
      type: 'midcta',
      title: 'Votre fichier Excel est-il en train de vous faire perdre du temps — ou pire, de l\'argent ?',
      content: 'Trezoone remplace votre tableau Excel par un outil connecté à votre banque, mis à jour automatiquement et partageable avec votre CA en un clic.',
    },
    {
      type: 'hook',
      title: 'Les limites d\'Excel que vous allez rencontrer',
      content: 'Excel fonctionne jusqu\'à un certain point. Voici les problèmes courants :',
      items: [
        'Formules cassées dès qu\'on ajoute ou supprime une ligne',
        'Pas de connexion avec votre banque : tout est ressaisi à la main',
        'Pas d\'alerte quand le solde prévu devient critique',
        'Partage difficile avec votre CA ou votre comptable',
        'Pas de suivi de l\'écart entre prévisionnel et réel',
      ],
    },
    {
      type: 'solution',
      title: 'Quand passer à Trezoone ?',
      content: 'Le bon moment pour passer d\'Excel à Trezoone, c\'est quand Excel prend plus de temps qu\'il n\'en fait gagner.',
      items: [
        'Vous avez plus de 2 sources de financement à suivre',
        'Vous passez plus de 2 heures par mois à mettre Excel à jour',
        'Vous avez eu une mauvaise surprise de trésorerie au moins une fois',
        'Votre CA demande des rapports réguliers et lisibles',
        'Vous voulez simuler des scénarios sans tout reconstruire',
      ],
    },
    {
      type: 'cluster',
      title: 'Ressources pour trésoriers d\'association',
      links: [
        { label: 'Logiciel trésorerie association', href: '/logiciel-tresorerie-association' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Modèle plan trésorerie', href: '/modele-plan-tresorerie' },
        { label: 'Tableau trésorerie gratuit', href: '/tableau-tresorerie-gratuit' },
        { label: 'Excel vs logiciel trésorerie', href: '/excel-vs-logiciel-tresorerie' },
        { label: 'Trésorerie association loi 1901', href: '/tresorerie-association-loi-1901' },
        { label: 'Simulation trésorerie association', href: '/simulation-tresorerie-association' },
        { label: 'Reporting financier association', href: '/reporting-financier-association' },
      ],
    },
    {
      type: 'cta',
      title: 'Téléchargez notre modèle ou testez Trezoone',
      content: 'Modèle Excel disponible dans notre espace ressources. Ou testez directement Trezoone, 14 jours gratuits.',
    },
  ],
  relatedLinks: [
    { label: 'Logiciel trésorerie association', href: '/logiciel-tresorerie-association' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Modèle plan trésorerie', href: '/modele-plan-tresorerie' },
    { label: 'Tableau trésorerie gratuit', href: '/tableau-tresorerie-gratuit' },
    { label: 'Excel vs logiciel trésorerie', href: '/excel-vs-logiciel-tresorerie' },
    { label: 'Trésorerie association loi 1901', href: '/tresorerie-association-loi-1901' },
    { label: 'Simulation trésorerie association', href: '/simulation-tresorerie-association' },
    { label: 'Subvention en retard', href: '/subvention-en-retard-tresorerie' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }
export default function ExcelTresorerieAssociationPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
